<template>
	<div data-tour-scope="ads">
		<v-main>
			<v-container fluid fill-height>
				<v-layout justify-center align-center>
					<v-card width="100%" outlined elevation-0>
						<v-toolbar flat class="border_bottom" shaped>
							<v-toolbar-title>Pubblicità</v-toolbar-title>
							<v-btn class="ml-6" outlined color="primary" data-tour="tutorial" @click="tour.start()"><v-icon left>mdi-school-outline</v-icon>Tutorial guidato</v-btn>
							<v-spacer></v-spacer>
							<v-toolbar-items>
								<v-menu offset-y left>
									<template v-slot:activator="{ on, attrs }">
										<v-btn text v-bind="attrs" data-tour="menu" title="Esporta e impostazioni" v-on="on"><v-icon>mdi-dots-vertical</v-icon></v-btn>
									</template>
									<v-list dense>
										<v-list-item link :disabled="!report || !report.campaigns.length" @click="esporta('excel')">
											<v-list-item-icon><v-icon small>mdi-arrow-collapse-down</v-icon></v-list-item-icon>
											<v-list-item-title>Esporta le campagne in Excel</v-list-item-title>
										</v-list-item>
										<v-list-item link :disabled="!report || !report.campaigns.length" @click="esporta('csv')">
											<v-list-item-icon><v-icon small>mdi-file-delimited-outline</v-icon></v-list-item-icon>
											<v-list-item-title>Esporta le campagne in CSV</v-list-item-title>
										</v-list-item>
										<v-list-item link @click="apriImpostazioni">
											<v-list-item-icon><v-icon small>mdi-tune-variant</v-icon></v-list-item-icon>
											<v-list-item-title>Piattaforme collegate</v-list-item-title>
										</v-list-item>
									</v-list>
								</v-menu>
							</v-toolbar-items>
						</v-toolbar>

						<v-col cols="12">
							<PeriodBar
								v-model="filters"
								:partial="!!(report && report.period.partial)"
								:loading="loading"
								:show-platform="false"
								:show-report="false"
								class="mb-3"
								data-tour="period"
								@refresh="carica"
							/>

							<v-alert v-if="error" type="error" dense outlined class="mb-3">{{ error }}</v-alert>

							<template v-if="report">
								<v-alert v-if="!report.sources.length" dense text type="info">
									Nessuna piattaforma collegata.
									<a href="#" @click.prevent="apriImpostazioni">Collega Google Ads, Meta, TikTok o Microsoft Ads</a> per leggere la spesa delle campagne.
								</v-alert>
								<template v-else>
									<v-alert v-for="w in report.warnings" :key="w.source" dense text type="warning" class="mb-2">
										{{ w.label }}: {{ w.status === 'error' ? `ultima lettura non riuscita (${w.error})` : 'non letta da più di due giorni' }}.
										<a href="#" @click.prevent="apriImpostazioni">Controlla il collegamento</a>
									</v-alert>
									<v-alert v-if="report.approxCurrency" dense text type="info" class="mb-2"> Alcuni giorni in valuta estera non hanno il cambio: gli importi sono presi così come sono. </v-alert>

									<AdsKpis :report="report" class="mb-1" />
									<v-row dense>
										<v-col cols="12">
											<AdsTrend :series="report.series" :compare="report.period.compare" />
										</v-col>
									</v-row>
									<AdsSources :rows="report.bySource" :breakeven-roas="report.current.breakevenRoas" class="mt-2" />
									<AdsCampaigns :items="report.campaigns" :search.sync="search" :breakeven-roas="report.current.breakevenRoas" class="mt-2" />
									<div class="ad-muted mt-2">
										Importi senza IVA, in valuta base. La spesa entra nel conto economico del report finanziario per le piattaforme che lo prevedono nelle impostazioni; per i mesi già fotografati usa
										«Aggiorna» nel report finanziario.
									</div>
								</template>
							</template>
							<v-skeleton-loader v-else-if="loading" type="card-heading, image, table" />
						</v-col>
					</v-card>
				</v-layout>
			</v-container>
		</v-main>
		<TourOverlay :tour="tour"></TourOverlay>
	</div>
</template>

<script>
import { mapActions, mapMutations } from 'vuex';
import TourOverlay from '@/components/core/tour/TourOverlay.vue';
import { useTour } from '@/components/core/tour/useTour';
import { createAdsTourSteps } from '@/components/ads/ads-tour';
import PeriodBar from '@/components/dashboard/PeriodBar.vue';
import { filtersFromQuery, filtersToParams } from '@/components/dashboard/filters';
import AdsKpis from '@/components/ads/AdsKpis.vue';
import AdsSources from '@/components/ads/AdsSources.vue';
import AdsCampaigns from '@/components/ads/AdsCampaigns.vue';

const AdsTrend = () => import('@/components/ads/AdsTrend.vue');

export default {
	name: 'AdsReport',
	components: { TourOverlay, PeriodBar, AdsKpis, AdsTrend, AdsSources, AdsCampaigns },
	setup() {
		return { tour: useTour(createAdsTourSteps()) };
	},
	data() {
		return {
			filters: filtersFromQuery(this.$route.query),
			search: this.$route.query.q || '',
			report: null,
			loading: false,
			error: '',
		};
	},
	watch: {
		filters(value, old) {
			if (JSON.stringify(filtersToParams(value)) === JSON.stringify(filtersToParams(old))) return;
			this.scriviUrl();
			this.carica();
		},
		search() {
			this.scriviUrl();
		},
	},
	created() {
		this.carica();
	},
	beforeDestroy() {
		this.tour.stop();
	},
	methods: {
		...mapMutations({ setLogout: 'profile/setLogout' }),
		...mapActions({ fetchReport: 'ads/fetchReport' }),
		params() {
			return filtersToParams({ ...this.filters, platform: '' });
		},
		// periodo e ricerca nell'URL: un refresh o il ritorno da un'altra pagina riaprono la stessa vista
		scriviUrl() {
			const query = { ...this.params(), ...(this.search ? { q: this.search } : {}) };
			const now = Object.fromEntries(Object.entries(this.$route.query).map(([k, v]) => [k, String(v)]));
			const next = Object.fromEntries(Object.entries(query).map(([k, v]) => [k, String(v)]));
			if (JSON.stringify(now) !== JSON.stringify(next)) this.$router.replace({ query }).catch(() => {});
		},
		async carica() {
			if (this.filters.period === 'custom' && (!this.filters.from || !this.filters.to)) return;
			this.loading = true;
			this.error = '';
			try {
				this.report = await this.fetchReport(this.params());
			} catch (err) {
				if (err.response && err.response.status === 401) {
					this.setLogout();
					this.$router.push('/login');
					return;
				}
				this.error = 'Impossibile caricare il report pubblicità. Riprova tra qualche istante.';
			} finally {
				this.loading = false;
			}
		},
		esporta(format) {
			this.exportAsync('adsreport', format, this.params());
		},
		// Impostazioni -> Pubblicita' e' una pagina di impostazioni come le altre (senza freccia di ritorno)
		apriImpostazioni() {
			this.$router.push('/setting/ads');
		},
	},
};
</script>

<style scoped>
.ad-muted {
	font-size: 12px;
	color: #5f6b7a;
}
</style>
